import React from "react";

/**
 * Country-aware landmark illustrations for the "Where are you?" card.
 *
 * Quality bar: modern flat-vector aesthetic with gradients, soft shadows,
 * layered shading, and proper depth — not Atari blocky.
 *
 * Each illustration is composed inside a 220×140 viewBox so they swap
 * cleanly. Sky + skyline silhouettes share the same palette so the card
 * feels cohesive when the user switches countries.
 *
 * Supported: US, FR, JP, IT, GB, ES, DE, MX, NL, GR, AU, BR, AE.
 * Anything else falls back to a globe + cloud scene.
 */
/**
 * Country-aware landmark illustrations for the "Where are you?" card.
 *
 * Quality bar: modern flat-vector aesthetic with gradients, soft shadows,
 * layered shading, and proper depth — not Atari blocky.
 *
 * Each illustration is composed inside a 220×140 viewBox so they swap
 * cleanly. Sky + skyline silhouettes share the same palette so the card
 * feels cohesive when the user switches countries.
 *
 * For countries without a dedicated hand-drawn landmark, we fall back
 * to the real rectangular SVG flag served from the public-domain
 * flagcdn.com CDN — fills the entire available area.
 */
// Countries with a hand-painted landmark JPG in /assets/landmarks/.
// When present, we render the image instead of the inline SVG so the
// art is consistent across the app and ships with the Capacitor bundle.
const LANDMARK_IMAGE_CODES = new Set([
  // Americas
  "US", "CA", "MX", "CR", "PA", "GT",
  "BR", "AR", "CL", "CO", "PE", "EC", "UY", "VE", "BS", "JM", "DO",
  // Caribbean (added Apr 2026)
  "PR", "BB", "KY", "TT", "AW",
  // Europe
  "GB", "FR", "IT", "ES", "DE", "NL", "CH", "SE", "NO", "DK",
  "AT", "BE", "PT", "GR", "TR", "PL", "CZ", "HU", "IE", "FI",
  "IS", "LU", "HR", "RO", "BG", "UA", "RU", "SK", "SI",
  "EE", "LV", "LT",
  // Asia & Pacific
  "JP", "KR", "CN", "HK", "TW", "SG", "TH", "VN", "ID", "PH",
  "MY", "IN", "PK", "BD", "LK", "NP", "KH", "LA", "MM", "AU", "NZ", "FJ",
  // Middle East & Africa
  "SA", "QA", "KW", "BH", "OM", "JO", "LB", "IL", "EG",
  "MA", "ZA", "KE", "NG", "ET", "AE",
]);

export function LandmarkIllustration({ code = "US", flag = "🌍", className = "" }) {
  const Landmark = LANDMARKS[code];

  // Painted-asset path: use the hand-illustrated tile when we have one.
  if (LANDMARK_IMAGE_CODES.has(code)) {
    return (
      <div
        className={`relative w-full h-full flex items-center justify-center ${className}`}
        aria-hidden
      >
        <img
          src={`/assets/landmarks/${code}.jpg`}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // No landmark for this country — show the rectangular flag instead.
  if (!Landmark) {
    const cc = (code || "us").toLowerCase();
    return (
      <div
        className={`relative w-full h-full flex items-center justify-center px-2 py-2 ${className}`}
        aria-hidden
      >
        {/* Bigger, centered, with breathing room from the card edge so the
            flag never clips against the card's overflow-hidden boundary.
            object-contain handles non-rectangular flags (Nepal, Switzerland,
            etc.) so they don't get cropped on the sides. */}
        <div
          className="bg-white p-1 rounded-md shadow-md ring-1 ring-navy/30 flex items-center justify-center"
          style={{ width: "min(85%, 140px)", height: "min(75%, 95px)" }}
        >
          <img
            src={`https://flagcdn.com/w640/${cc}.png`}
            srcSet={`https://flagcdn.com/w640/${cc}.png 1x, https://flagcdn.com/w1280/${cc}.png 2x`}
            alt={`${code} flag`}
            loading="lazy"
            className="max-w-full max-h-full object-contain rounded-sm"
          />
        </div>
      </div>
    );
  }

  // Dedicated hand-drawn landmark SVG.
  return (
    <svg
      viewBox="0 0 220 140"
      className={className}
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EAF4FF" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="liberty" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7FCBA9" />
          <stop offset="100%" stopColor="#4A9479" />
        </linearGradient>
        <linearGradient id="libertyShade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#3A8268" />
          <stop offset="100%" stopColor="#5BA68A" />
        </linearGradient>
        <linearGradient id="eiffelGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7A6650" />
          <stop offset="100%" stopColor="#4F3F2C" />
        </linearGradient>
        <linearGradient id="goldGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFE099" />
          <stop offset="100%" stopColor="#E8AA3A" />
        </linearGradient>
        <linearGradient id="fujiTop" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F8FAFF" />
          <stop offset="100%" stopColor="#9EC8E8" />
        </linearGradient>
        <linearGradient id="fujiBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7BA8D6" />
          <stop offset="100%" stopColor="#3F6FA8" />
        </linearGradient>
        <linearGradient id="britBus" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E63A2D" />
          <stop offset="100%" stopColor="#B82A1F" />
        </linearGradient>
        <linearGradient id="stoneGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E0CFA8" />
          <stop offset="100%" stopColor="#B89A6A" />
        </linearGradient>
        <linearGradient id="pyramid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D4A66B" />
          <stop offset="100%" stopColor="#8C6539" />
        </linearGradient>
        <radialGradient id="globeGrad" cx="0.4" cy="0.4" r="0.7">
          <stop offset="0%" stopColor="#88C1F2" />
          <stop offset="100%" stopColor="#3B7AC4" />
        </radialGradient>
      </defs>

      {/* Soft sky gradient backdrop */}
      <rect x="0" y="0" width="220" height="140" fill="url(#sky)" />

      {/* Stylized clouds (shared) */}
      <g fill="#DCE9F8" opacity="0.85">
        <ellipse cx="35" cy="20" rx="14" ry="4" />
        <ellipse cx="44" cy="18" rx="9" ry="3" />
        <ellipse cx="175" cy="24" rx="13" ry="3.5" />
        <ellipse cx="185" cy="22" rx="8" ry="2.5" />
      </g>

      <Landmark />
    </svg>
  );
}

const LANDMARKS = {
  /* ---------------- UNITED STATES — Statue of Liberty + NYC skyline ---------------- */
  US: () => (
    <g>
      {/* Light skyline behind */}
      <g fill="#B4CFEC">
        <rect x="20" y="70" width="11" height="55" />
        <rect x="33" y="60" width="14" height="65" />
        <rect x="50" y="78" width="10" height="47" />
        <rect x="62" y="50" width="16" height="75" />
        <rect x="82" y="65" width="11" height="60" />
        <rect x="96" y="42" width="18" height="83" />
        <path d="M105 42 L105 28 L106 28 L107 30 L107 42" />
        <rect x="118" y="68" width="10" height="57" />
      </g>
      {/* Darker buildings mid-layer */}
      <g fill="#7FA4D0" opacity="0.55">
        <rect x="44" y="80" width="6" height="45" />
        <rect x="78" y="72" width="6" height="53" />
        <rect x="114" y="74" width="7" height="51" />
      </g>

      {/* Statue of Liberty — right side, hero */}
      <g>
        {/* Pedestal */}
        <rect x="158" y="106" width="32" height="22" fill="#8B6A4A" rx="1" />
        <rect x="158" y="106" width="32" height="3" fill="#A48360" />
        <rect x="160" y="112" width="28" height="2" fill="#6E5234" opacity="0.5" />
        <rect x="163" y="118" width="5" height="10" fill="#5C4528" />
        <rect x="174" y="118" width="5" height="10" fill="#5C4528" />
        <rect x="185" y="118" width="3" height="10" fill="#5C4528" />
        {/* Robe — main body */}
        <path d="M158 106 L166 70 L174 66 L182 70 L190 106 Z" fill="url(#liberty)" />
        {/* Robe folds (shading) */}
        <path d="M160 106 L166 80 L172 78 L172 106 Z" fill="url(#libertyShade)" opacity="0.45" />
        <path d="M176 106 L182 80 L188 84 L189 106 Z" fill="#3A8268" opacity="0.35" />
        <path d="M168 105 L170 90 L173 105 Z" fill="#FFFFFF" opacity="0.12" />
        {/* Tablet */}
        <rect x="150" y="84" width="11" height="14" fill="#5BA68A" rx="1" stroke="#3A8268" strokeWidth="0.4" />
        <line x1="153" y1="88" x2="158" y2="88" stroke="#3A8268" strokeWidth="0.4" />
        <line x1="153" y1="91" x2="158" y2="91" stroke="#3A8268" strokeWidth="0.4" />
        <line x1="153" y1="94" x2="158" y2="94" stroke="#3A8268" strokeWidth="0.4" />
        {/* Arm holding torch */}
        <path d="M180 68 L188 38 L192 30" stroke="url(#liberty)" strokeWidth="4" strokeLinecap="round" fill="none" />
        {/* Torch holder */}
        <ellipse cx="191" cy="29" rx="3" ry="2" fill="#5BA68A" />
        {/* Flame */}
        <path d="M188 28 Q189 18 191 22 Q193 16 194 22 Q195 18 196 28 Z" fill="url(#goldGrad)" />
        <path d="M190 26 Q191 20 192 24 Q193 20 194 26 Z" fill="#FFF4D0" />
        {/* Head + neck */}
        <ellipse cx="174" cy="58" rx="5" ry="6" fill="url(#liberty)" />
        <path d="M171 64 L177 64 L176 68 L172 68 Z" fill="url(#liberty)" />
        {/* Face shading */}
        <path d="M173 56 Q174 56 175 56 Q176 58 175 60 Q173 60 173 58 Z" fill="#3A8268" opacity="0.3" />
        {/* Crown spikes */}
        <g fill="url(#liberty)" stroke="#3A8268" strokeWidth="0.3">
          <path d="M168 54 L166 47 L170 52 Z" />
          <path d="M171 51 L170 44 L173 50 Z" />
          <path d="M173.5 50 L174 42 L175 50 Z" />
          <path d="M176 50 L177 44 L177.5 51 Z" />
          <path d="M178 51 L181 45 L180 52 Z" />
        </g>
      </g>
    </g>
  ),

  /* ---------------- FRANCE — Eiffel Tower ---------------- */
  FR: () => (
    <g>
      {/* Paris buildings */}
      <g fill="#B4CFEC">
        <rect x="20" y="92" width="16" height="35" />
        <path d="M22 92 L28 86 L34 92 Z" />
        <rect x="38" y="86" width="18" height="42" />
        <rect x="58" y="100" width="14" height="28" />
        <path d="M60 100 L65 94 L70 100 Z" />
        <rect x="135" y="90" width="20" height="38" />
        <path d="M137 90 L145 80 L153 90 Z" fill="#9EBEDC" />
        <rect x="157" y="100" width="14" height="28" />
        <rect x="174" y="92" width="20" height="36" />
        <rect x="196" y="100" width="14" height="28" />
      </g>
      {/* Eiffel Tower — center */}
      <g>
        {/* Base legs */}
        <path d="M80 124 Q84 120 88 110 L97 110 Q95 120 95 124 Z" fill="url(#eiffelGrad)" />
        <path d="M127 124 Q123 120 119 110 L110 110 Q112 120 112 124 Z" fill="url(#eiffelGrad)" />
        {/* Arch */}
        <path d="M88 110 Q104 100 119 110" stroke="#5C4A35" strokeWidth="1.2" fill="none" />
        {/* Lower body */}
        <path d="M93 110 L97 80 L110 80 L114 110 Z" fill="url(#eiffelGrad)" />
        <line x1="96" y1="85" x2="111" y2="85" stroke="#3D3022" strokeWidth="0.4" />
        <line x1="95" y1="92" x2="112" y2="92" stroke="#3D3022" strokeWidth="0.4" />
        <line x1="94" y1="99" x2="113" y2="99" stroke="#3D3022" strokeWidth="0.4" />
        {/* Mid platform */}
        <rect x="95" y="78" width="17" height="3" fill="#5C4A35" />
        {/* Middle */}
        <path d="M98 78 L100 56 L107 56 L109 78 Z" fill="url(#eiffelGrad)" />
        <line x1="99" y1="62" x2="108" y2="62" stroke="#3D3022" strokeWidth="0.4" />
        <line x1="99" y1="68" x2="108" y2="68" stroke="#3D3022" strokeWidth="0.4" />
        <rect x="98" y="54" width="11" height="3" fill="#5C4A35" />
        {/* Upper */}
        <path d="M101 54 L102 36 L105 36 L106 54 Z" fill="url(#eiffelGrad)" />
        {/* Spire */}
        <path d="M102 36 L103.5 22 L105 36 Z" fill="#5C4A35" />
        <line x1="103.5" y1="22" x2="103.5" y2="14" stroke="#5C4A35" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="103.5" cy="14" r="1.2" fill="url(#goldGrad)" />
      </g>
    </g>
  ),

  /* ---------------- JAPAN — Mt. Fuji + Torii ---------------- */
  JP: () => (
    <g>
      {/* Distant hills */}
      <path d="M0 110 Q40 100 80 108 Q120 116 160 106 Q200 100 220 108 L220 128 L0 128 Z" fill="#C8D9EE" />
      {/* Mt Fuji */}
      <path d="M55 110 L100 35 L145 110 Z" fill="url(#fujiBody)" />
      {/* Snow cap */}
      <path d="M83 65 L100 35 L117 65 Q113 62 110 66 Q108 62 104 64 Q100 60 96 64 Q92 62 89 66 Q86 62 83 65 Z" fill="url(#fujiTop)" />
      {/* Snow streaks */}
      <path d="M89 72 L96 80" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
      <path d="M103 72 L106 82" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
      <path d="M111 72 L116 80" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
      {/* Side shading */}
      <path d="M100 35 L100 110 L145 110 Z" fill="#000" opacity="0.08" />
      {/* Torii gate (right) */}
      <g>
        <rect x="170" y="78" width="3" height="42" fill="#C53030" />
        <rect x="195" y="78" width="3" height="42" fill="#C53030" />
        <rect x="166" y="78" width="36" height="3.5" fill="#7A1818" />
        <path d="M163 73 Q183 64 205 73 L205 78 L163 78 Z" fill="#C53030" />
        <path d="M163 73 Q183 67 205 73" stroke="#7A1818" strokeWidth="0.6" fill="none" />
        <rect x="168" y="85" width="32" height="2.4" fill="#7A1818" />
      </g>
      {/* Sun */}
      <circle cx="40" cy="38" r="9" fill="#E63A2D" opacity="0.85" />
      <circle cx="40" cy="38" r="9" fill="url(#goldGrad)" opacity="0.4" />
    </g>
  ),

  /* ---------------- ITALY — Colosseum + cypress ---------------- */
  IT: () => (
    <g>
      <g fill="#B4CFEC" opacity="0.85">
        <rect x="20" y="98" width="14" height="30" />
        <rect x="172" y="96" width="18" height="32" />
        <path d="M174 96 L181 88 L188 96 Z" />
        <rect x="196" y="102" width="14" height="26" />
      </g>
      {/* Cypress trees */}
      <g fill="#3A6B41">
        <ellipse cx="50" cy="112" rx="4" ry="20" />
        <rect x="48.5" y="124" width="3" height="5" fill="#5C4528" />
      </g>
      {/* Colosseum */}
      <g>
        <ellipse cx="115" cy="110" rx="55" ry="14" fill="#A88A5C" opacity="0.5" />
        <path d="M65 110 Q65 78 115 76 Q165 78 165 110 Z" fill="url(#stoneGrad)" />
        <path d="M65 110 Q65 78 115 76 Q165 78 165 110" stroke="#85673F" strokeWidth="0.5" fill="none" />
        {/* Arches row 1 */}
        <g fill="#7A5E3B">
          {Array.from({ length: 8 }).map((_, i) => {
            const x = 70 + i * 12;
            return <path key={i} d={`M${x} 110 V96 Q${x + 4} 90 ${x + 8} 96 V110 Z`} fillOpacity="0.55" />;
          })}
        </g>
        {/* Row 2 */}
        <g fill="#5C4528" opacity="0.45">
          {Array.from({ length: 7 }).map((_, i) => {
            const x = 72 + i * 12;
            return <path key={i} d={`M${x} 96 V86 Q${x + 4} 80 ${x + 8} 86 V96 Z`} />;
          })}
        </g>
        {/* Top crumble */}
        <path d="M85 78 L88 76 L92 78 M96 76 L100 75 L104 76 M118 75 L124 76" stroke="#85673F" strokeWidth="1" fill="none" />
      </g>
    </g>
  ),

  /* ---------------- UNITED KINGDOM — Big Ben + bus ---------------- */
  GB: () => (
    <g>
      <g fill="#B4CFEC">
        <rect x="20" y="92" width="16" height="36" />
        <rect x="38" y="98" width="12" height="30" />
        <rect x="170" y="96" width="14" height="32" />
        <rect x="188" y="92" width="20" height="36" />
        <path d="M190 92 L198 84 L206 92 Z" />
      </g>
      {/* Big Ben — center-left */}
      <g>
        <rect x="84" y="42" width="22" height="80" fill="url(#stoneGrad)" />
        <rect x="84" y="42" width="22" height="2" fill="#85673F" />
        <rect x="86" y="56" width="18" height="14" fill="#fff" />
        <circle cx="95" cy="63" r="6" fill="#FFF8E1" stroke="#5C4528" strokeWidth="0.6" />
        <line x1="95" y1="63" x2="95" y2="59" stroke="#0E2C54" strokeWidth="0.8" strokeLinecap="round" />
        <line x1="95" y1="63" x2="98" y2="63" stroke="#0E2C54" strokeWidth="0.6" strokeLinecap="round" />
        <circle cx="95" cy="63" r="0.8" fill="#0E2C54" />
        {/* Upper architecture */}
        <rect x="86" y="34" width="18" height="8" fill="url(#stoneGrad)" />
        <path d="M88 34 L95 24 L102 34 Z" fill="#85673F" />
        <line x1="95" y1="24" x2="95" y2="14" stroke="#85673F" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="95" cy="14" r="1.4" fill="url(#goldGrad)" />
        {/* Brickwork lines */}
        <line x1="84" y1="80" x2="106" y2="80" stroke="#85673F" strokeWidth="0.3" />
        <line x1="84" y1="100" x2="106" y2="100" stroke="#85673F" strokeWidth="0.3" />
      </g>
      {/* Double-decker bus */}
      <g>
        <rect x="135" y="100" width="42" height="22" rx="3" fill="url(#britBus)" />
        <rect x="139" y="104" width="6" height="6" fill="#FFF8E1" />
        <rect x="148" y="104" width="6" height="6" fill="#FFF8E1" />
        <rect x="157" y="104" width="6" height="6" fill="#FFF8E1" />
        <rect x="166" y="104" width="6" height="6" fill="#FFF8E1" />
        <rect x="139" y="113" width="33" height="5" fill="#7A1818" opacity="0.5" />
        <circle cx="143" cy="122" r="3" fill="#0E2C54" />
        <circle cx="143" cy="122" r="1" fill="#fff" />
        <circle cx="169" cy="122" r="3" fill="#0E2C54" />
        <circle cx="169" cy="122" r="1" fill="#fff" />
      </g>
    </g>
  ),

  /* ---------------- SPAIN — Sagrada Familia spires ---------------- */
  ES: () => (
    <g>
      <g fill="#B4CFEC">
        <rect x="20" y="98" width="14" height="30" />
        <rect x="195" y="98" width="16" height="30" />
      </g>
      {/* Sagrada spires (4 spires of varying heights) */}
      <g fill="url(#stoneGrad)">
        <path d="M60 124 L65 32 L70 124 Z" />
        <path d="M82 124 L88 22 L94 124 Z" />
        <path d="M104 124 L112 40 L120 124 Z" />
        <path d="M130 124 L136 28 L142 124 Z" />
        <path d="M152 124 L158 44 L164 124 Z" />
      </g>
      {/* Spire decorations */}
      <g fill="#E8AA3A">
        <circle cx="65" cy="34" r="2" />
        <circle cx="88" cy="24" r="2.5" />
        <circle cx="112" cy="42" r="3" />
        <circle cx="136" cy="30" r="2.5" />
        <circle cx="158" cy="46" r="2" />
      </g>
      <g stroke="#85673F" strokeWidth="0.3" fill="none">
        <line x1="63" y1="60" x2="68" y2="60" />
        <line x1="62" y1="78" x2="69" y2="78" />
        <line x1="61" y1="96" x2="70" y2="96" />
        <line x1="86" y1="60" x2="92" y2="60" />
        <line x1="85" y1="80" x2="93" y2="80" />
        <line x1="109" y1="70" x2="116" y2="70" />
        <line x1="107" y1="92" x2="118" y2="92" />
        <line x1="133" y1="60" x2="140" y2="60" />
        <line x1="131" y1="86" x2="142" y2="86" />
      </g>
    </g>
  ),

  /* ---------------- GERMANY — Brandenburg Gate ---------------- */
  DE: () => (
    <g>
      <g fill="#B4CFEC">
        <rect x="20" y="96" width="20" height="32" />
        <rect x="180" y="92" width="22" height="36" />
        <path d="M182 92 L191 84 L200 92 Z" />
      </g>
      {/* Brandenburg Gate */}
      <g>
        <rect x="50" y="44" width="120" height="78" fill="url(#stoneGrad)" />
        {/* Pediment top */}
        <rect x="48" y="40" width="124" height="6" fill="#85673F" />
        {/* Columns (6) */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <rect
            key={i}
            x={56 + i * 18}
            y="50"
            width="9"
            height="68"
            fill="#fff"
            stroke="#85673F"
            strokeWidth="0.3"
          />
        ))}
        {/* Column lines */}
        <g stroke="#C2A573" strokeWidth="0.3">
          {[0, 1, 2, 3, 4, 5].flatMap((i) =>
            [54, 62, 70, 78, 86, 94, 102, 110].map((y) => (
              <line key={`${i}-${y}`} x1={58 + i * 18} y1={y} x2={63 + i * 18} y2={y} />
            ))
          )}
        </g>
        {/* Chariot statue (small) */}
        <rect x="100" y="32" width="20" height="8" fill="#8B6A4A" />
        <rect x="98" y="28" width="24" height="4" fill="#5C4528" />
        <circle cx="106" cy="36" r="2" fill="#5C4528" />
        <circle cx="114" cy="36" r="2" fill="#5C4528" />
      </g>
    </g>
  ),

  /* ---------------- MEXICO — Pyramid + cacti ---------------- */
  MX: () => (
    <g>
      {/* Distant mountains */}
      <path d="M0 110 L40 90 L80 105 L120 95 L160 108 L200 95 L220 105 L220 128 L0 128 Z" fill="#B4CFEC" />
      {/* Sun */}
      <circle cx="180" cy="36" r="14" fill="url(#goldGrad)" />
      {/* Pyramid */}
      <g>
        <path d="M60 116 L100 50 L140 116 Z" fill="url(#pyramid)" />
        {/* Steps shadow */}
        <g fill="#5C4220" opacity="0.35">
          <rect x="76" y="92" width="48" height="2.5" />
          <rect x="80" y="80" width="40" height="2.5" />
          <rect x="84" y="68" width="32" height="2.5" />
          <rect x="88" y="56" width="24" height="2.5" />
        </g>
        {/* Top temple */}
        <rect x="94" y="44" width="12" height="8" fill="#A07842" />
        <rect x="93" y="42" width="14" height="3" fill="#5C4220" />
      </g>
      {/* Cactus */}
      <g fill="#3A6B41">
        <rect x="34" y="92" width="6" height="32" rx="2" />
        <path d="M40 102 Q45 100 45 95 Q45 88 41 87 L41 102 Z" />
        <path d="M34 108 Q29 106 29 100 L33 100 L33 108 Z" />
      </g>
      {/* Flowers on cactus */}
      <circle cx="37" cy="93" r="1.5" fill="#E63A2D" />
      <circle cx="44" cy="89" r="1.2" fill="#FFD980" />
    </g>
  ),

  /* ---------------- AUSTRALIA — Opera House + harbour bridge ---------------- */
  AU: () => (
    <g>
      {/* Water */}
      <rect x="0" y="110" width="220" height="18" fill="#88C1F2" opacity="0.6" />
      {/* Harbour bridge */}
      <g fill="none" stroke="#5C4528" strokeWidth="2">
        <path d="M138 92 Q172 60 206 92" />
        <line x1="138" y1="92" x2="138" y2="108" />
        <line x1="206" y1="92" x2="206" y2="108" />
      </g>
      <g stroke="#5C4528" strokeWidth="0.6">
        <line x1="146" y1="78" x2="146" y2="108" />
        <line x1="156" y1="71" x2="156" y2="108" />
        <line x1="166" y1="65" x2="166" y2="108" />
        <line x1="176" y1="62" x2="176" y2="108" />
        <line x1="186" y1="65" x2="186" y2="108" />
        <line x1="196" y1="72" x2="196" y2="78" />
      </g>
      <rect x="138" y="106" width="68" height="2" fill="#5C4528" />
      {/* Opera House */}
      <g fill="#fff" stroke="#0E2C54" strokeWidth="0.4">
        <path d="M30 110 Q40 80 60 80 L70 110 Z" />
        <path d="M50 110 Q60 76 78 76 L88 110 Z" />
        <path d="M70 110 Q80 72 96 72 L106 110 Z" />
        <path d="M90 110 Q100 80 116 80 L122 110 Z" />
      </g>
      <g fill="#B4CFEC" opacity="0.7">
        <path d="M30 110 Q40 80 60 80 L70 110 Z" opacity="0.4" />
        <path d="M70 110 Q80 72 96 72 L106 110 Z" opacity="0.4" />
      </g>
    </g>
  ),

  /* ---------------- BRAZIL — Christ the Redeemer + mountains ---------------- */
  BR: () => (
    <g>
      {/* Sugarloaf */}
      <path d="M0 120 Q30 90 60 118 Q90 90 120 118 Q150 95 180 118 Q210 95 220 110 L220 128 L0 128 Z" fill="#7BA88A" />
      <path d="M0 120 Q30 100 60 122 L0 128 Z" fill="#5A8569" opacity="0.5" />
      {/* Christ the Redeemer statue */}
      <g fill="#fff" stroke="#0E2C54" strokeWidth="0.3">
        {/* Pedestal */}
        <rect x="138" y="62" width="14" height="14" fill="#B89A6A" />
        {/* Body */}
        <path d="M141 62 L143 38 L148 38 L150 62 Z" />
        {/* Arms */}
        <rect x="125" y="40" width="40" height="3" rx="1" />
        {/* Head */}
        <circle cx="145.5" cy="34" r="3" />
      </g>
      {/* Beach palm */}
      <g>
        <path d="M40 124 Q39 100 36 88" stroke="#5C4528" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M36 88 Q26 84 22 88 Q26 90 36 88 M36 88 Q46 84 50 88 Q46 90 36 88 M36 88 Q34 78 30 76 Q34 80 36 88 M36 88 Q38 78 42 78 Q38 82 36 88" fill="#3A6B41" />
      </g>
    </g>
  ),

  /* ---------------- UAE / DUBAI — Burj Khalifa + dunes ---------------- */
  AE: () => (
    <g>
      {/* Dunes */}
      <path d="M0 120 Q40 102 80 118 Q120 100 160 116 Q200 102 220 118 L220 128 L0 128 Z" fill="#E8C68A" />
      <path d="M0 120 Q40 105 80 121 L0 128 Z" fill="#B89A6A" opacity="0.4" />
      {/* Sun */}
      <circle cx="40" cy="34" r="12" fill="url(#goldGrad)" />
      {/* Burj Khalifa */}
      <g>
        <path d="M122 120 L128 100 L132 65 L136 38 L140 65 L144 100 L150 120 Z" fill="#88C1F2" opacity="0.4" />
        <path d="M124 120 L128 90 L132 60 L136 30 L140 60 L144 90 L148 120 Z" fill="#5BA0D8" />
        <line x1="136" y1="30" x2="136" y2="14" stroke="#5BA0D8" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="136" cy="14" r="1.4" fill="#fff" />
      </g>
    </g>
  ),

  /* (Removed — fallback now lives in <FlagFallback /> outside LANDMARKS map.) */
};

// Sensible reuses where landmarks would feel right.
LANDMARKS.NZ = LANDMARKS.AU;   // Sydney Opera House feels close enough for Oceania
LANDMARKS.AT = LANDMARKS.DE;   // Reuse Brandenburg/columns feel
LANDMARKS.BE = LANDMARKS.DE;
LANDMARKS.CH = LANDMARKS.DE;
LANDMARKS.PT = LANDMARKS.ES;
LANDMARKS.GR = LANDMARKS.ES;
LANDMARKS.IE = LANDMARKS.GB;

/**
 * (Old FlagFallback removed — flags are now served as real SVG/PNG
 * rectangles by flagcdn.com inside <LandmarkIllustration />.)
 */

/**
 * Orange + green suitcases illustration. Modern flat-vector style with
 * highlights and shadow layering for the My Trips promo card.
 */
export function SuitcasesIllustration({ className = "" }) {
  return (
    <svg
      viewBox="0 0 90 90"
      className={className}
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="orangeBag" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F47A4A" />
          <stop offset="100%" stopColor="#C94D23" />
        </linearGradient>
        <linearGradient id="greenBag" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5BA68A" />
          <stop offset="100%" stopColor="#2E7D5A" />
        </linearGradient>
      </defs>

      <circle cx="45" cy="48" r="36" fill="#EAF2FB" />

      {/* Back green suitcase */}
      <g>
        <rect x="42" y="40" width="30" height="26" rx="3.5" fill="url(#greenBag)" stroke="#0E2C54" strokeWidth="1.4" />
        <rect x="42" y="48" width="30" height="2.5" fill="#0E2C54" opacity="0.25" />
        <rect x="42" y="40" width="30" height="3" fill="#fff" opacity="0.18" />
        <rect x="53" y="33" width="8" height="8" rx="1.5" fill="none" stroke="#0E2C54" strokeWidth="1.4" />
        <rect x="54" y="34" width="6" height="2" fill="#0E2C54" opacity="0.3" />
        <circle cx="49" cy="65" r="3" fill="#0E2C54" />
        <circle cx="65" cy="65" r="3" fill="#0E2C54" />
        <circle cx="49" cy="65" r="1.2" fill="#fff" opacity="0.4" />
        <circle cx="65" cy="65" r="1.2" fill="#fff" opacity="0.4" />
        {/* Sticker */}
        <circle cx="63" cy="54" r="2.5" fill="#FFD980" />
        <path d="M62 53 L64 53 L63 55 Z" fill="#E63A2D" />
      </g>

      {/* Front orange suitcase */}
      <g>
        <rect x="14" y="44" width="34" height="30" rx="4" fill="url(#orangeBag)" stroke="#0E2C54" strokeWidth="1.5" />
        <rect x="14" y="52" width="34" height="2.5" fill="#0E2C54" opacity="0.25" />
        <rect x="14" y="44" width="34" height="3" fill="#fff" opacity="0.22" />
        <rect x="26" y="36" width="10" height="9" rx="2" fill="none" stroke="#0E2C54" strokeWidth="1.5" />
        <rect x="27" y="37" width="8" height="3" fill="#0E2C54" opacity="0.3" />
        <circle cx="21" cy="73" r="3.5" fill="#0E2C54" />
        <circle cx="41" cy="73" r="3.5" fill="#0E2C54" />
        <circle cx="21" cy="73" r="1.5" fill="#fff" opacity="0.4" />
        <circle cx="41" cy="73" r="1.5" fill="#fff" opacity="0.4" />
        {/* Stickers */}
        <rect x="29" y="60" width="8" height="4" rx="0.6" fill="#FFD980" />
        <text
          x="33"
          y="63.4"
          textAnchor="middle"
          fontSize="3"
          fontWeight="800"
          fill="#C94D23"
          fontFamily="Plus Jakarta Sans, sans-serif"
        >
          ROMA
        </text>
        <circle cx="22" cy="62" r="2" fill="#5BA68A" />
        <path d="M21 62 L22 63 L24 60.5" stroke="#fff" strokeWidth="0.6" fill="none" />
      </g>
    </svg>
  );
}

/**
 * Travel Vault hero illustration — uses the v1.3 hand-painted asset
 * (open navy safe with passport book, boarding pass, and hotel postcard).
 * The asset lives at /assets/travel-vault.jpg in the public bundle so it
 * also ships with the Capacitor iOS build.
 */
export function VaultIllustration({ className = "" }) {
  return (
    <img
      src="/assets/travel-vault.jpg"
      alt=""
      aria-hidden
      loading="lazy"
      className={`object-contain w-full h-full ${className}`}
      style={{ mixBlendMode: "multiply" }}
    />
  );
}
